/**
 * KMD closed vocabularies and value tables (spec §5). These sets are frozen
 * API for `khiyarsa: 1` — additions are allowed, meaning changes are not.
 */
import type {
  ArrowheadName,
  Cardinality,
  Direction,
  Family,
  NodeKind,
  Routing,
  Spacing,
  StyleProps,
  Theme,
} from "./types";

export const FAMILIES: readonly Family[] = [
  "flow",
  "swimlane",
  "architecture",
  "sequence",
  "mindmap",
  "tree",
  "er",
  "timeline",
];

export const NODE_KINDS: readonly NodeKind[] = [
  "process",
  "decision",
  "start",
  "end",
  "subprocess",
  "database",
  "queue",
  "service",
  "api",
  "actor",
  "user",
  "external",
  "event",
  "note",
];

export const EDGE_FLAGS = ["dashed", "dotted", "thick", "thin"] as const;

export const ARROWHEADS: readonly ArrowheadName[] = [
  "none",
  "arrow",
  "triangle",
  "triangle-open",
  "circle",
  "circle-open",
  "diamond",
  "diamond-open",
  "bar",
];

export const CARDINALITIES: readonly Cardinality[] = ["1", "0..1", "n", "0..n", "1..n"];

export const DIRECTIONS: readonly Direction[] = ["down", "right", "left", "up"];
export const THEMES: readonly Theme[] = ["default", "mono", "vibrant"];
export const SPACINGS: readonly Spacing[] = ["compact", "comfortable", "spacious"];
export const ROUTINGS: readonly Routing[] = ["auto", "straight", "curved", "orthogonal"];

/** Per-family metadata (canonical; parse.ts and the machine spec read this). */
export const FAMILY_INFO: Record<
  Family,
  {
    defaultDirection: Direction;
    container: string;
    maxHeadingDepth: number;
    autoChain: boolean;
    sectionBreak: boolean;
  }
> = {
  flow: { defaultDirection: "down", container: "stage", maxHeadingDepth: 2, autoChain: true, sectionBreak: true },
  swimlane: { defaultDirection: "right", container: "lane", maxHeadingDepth: 2, autoChain: false, sectionBreak: true },
  architecture: { defaultDirection: "right", container: "zone/subsystem", maxHeadingDepth: 3, autoChain: false, sectionBreak: false },
  sequence: { defaultDirection: "right", container: "participant", maxHeadingDepth: 2, autoChain: false, sectionBreak: false },
  mindmap: { defaultDirection: "right", container: "branch (outline)", maxHeadingDepth: 6, autoChain: false, sectionBreak: false },
  tree: { defaultDirection: "down", container: "branch (outline)", maxHeadingDepth: 6, autoChain: false, sectionBreak: false },
  er: { defaultDirection: "right", container: "entity", maxHeadingDepth: 2, autoChain: false, sectionBreak: false },
  timeline: { defaultDirection: "right", container: "period", maxHeadingDepth: 2, autoChain: false, sectionBreak: false },
};

/** Directive vocabulary (canonical; parse.ts and the machine spec read this). */
export const DIRECTIVES: Record<
  string,
  { families: readonly Family[]; separator?: string; meaning: string }
> = {
  group: {
    families: ["flow", "architecture"],
    meaning: "explicit container where a heading level isn't available",
  },
  loop: { families: ["flow", "sequence"], meaning: "loop frame" },
  alt: { families: ["sequence"], separator: "else", meaning: "alternatives frame" },
  opt: { families: ["sequence"], meaning: "optional frame" },
  par: { families: ["sequence"], separator: "and", meaning: "parallel frame" },
};

export const BUILTIN_TAGS = [
  "primary",
  "success",
  "warning",
  "danger",
  "muted",
  "external",
  "highlight",
  "unresolved",
] as const;

/** style property key (KMD spelling) → allowed values ("*" = free-form validated elsewhere) */
export const STYLE_KEYS: Record<string, readonly string[] | "*"> = {
  fill: "*",
  stroke: "*",
  "text-color": "*",
  "fill-pattern": ["solid", "hachure", "cross-hatch", "zigzag"],
  line: ["solid", "dashed", "dotted"],
  width: ["thin", "medium", "bold"],
  sketch: ["none", "normal", "high"],
  rounded: ["true", "false"],
  opacity: "*",
  font: ["hand", "sans", "display", "comic", "qomra"],
  "font-size": ["s", "m", "l", "xl"],
  "text-align": ["left", "center", "right"],
  shape: ["rect", "rounded", "ellipse", "diamond"],
};

/** style keys whose value is a color token (palette name, named color, or #hex) */
export const COLOR_STYLE_KEYS = ["fill", "stroke", "text-color"] as const;

/** KMD style key → StyleProps property */
export const STYLE_KEY_TO_PROP: Record<string, keyof StyleProps> = {
  fill: "fill",
  stroke: "stroke",
  "text-color": "textColor",
  "fill-pattern": "fillPattern",
  line: "line",
  width: "width",
  sketch: "sketch",
  rounded: "rounded",
  opacity: "opacity",
  font: "font",
  "font-size": "fontSize",
  "text-align": "textAlign",
  shape: "shape",
};

// "direction" was accepted (and silently ignored) here until spec 1.0-rc.1;
// per-container direction is not implemented, so it now fails loudly in
// attributes.ts rather than pretending to work.
export const NODE_KEYS = ["sub", "icon", "order"] as const;
export const EDGE_KEYS = ["start", "end", "both", "start-label", "end-label"] as const;

export const SPACING_PX: Record<Spacing, number> = {
  compact: 24,
  comfortable: 44,
  spacious: 72,
};

/**
 * Full open-color scale (https://github.com/yeun/open-color, MIT) for the
 * `name.1`–`name.9` tokens; Excalidraw's COLOR_PALETTE only carries 5 weights.
 * Index 0 (weight 50) is addressed as `.1`, …, index 9 (weight 900) as `.9`+1:
 * token digit d maps to scale[d] (so `.1`→100 … `.9`→900; bare name is themed).
 */
export const OPEN_COLOR_SCALE: Record<string, readonly string[]> = {
  gray: ["#f8f9fa", "#f1f3f5", "#e9ecef", "#dee2e6", "#ced4da", "#adb5bd", "#868e96", "#495057", "#343a40", "#212529"],
  red: ["#fff5f5", "#ffe3e3", "#ffc9c9", "#ffa8a8", "#ff8787", "#ff6b6b", "#fa5252", "#f03e3e", "#e03131", "#c92a2a"],
  pink: ["#fff0f6", "#ffdeeb", "#fcc2d7", "#faa2c1", "#f783ac", "#f06595", "#e64980", "#d6336c", "#c2255c", "#a61e4d"],
  grape: ["#f8f0fc", "#f3d9fa", "#eebefa", "#e599f7", "#da77f2", "#cc5de8", "#be4bdb", "#ae3ec9", "#9c36b5", "#862e9c"],
  violet: ["#f3f0ff", "#e5dbff", "#d0bfff", "#b197fc", "#9775fa", "#845ef7", "#7950f2", "#7048e8", "#6741d9", "#5f3dc4"],
  blue: ["#e7f5ff", "#d0ebff", "#a5d8ff", "#74c0fc", "#4dabf7", "#339af0", "#228be6", "#1c7ed6", "#1971c2", "#1864ab"],
  cyan: ["#e3fafc", "#c5f6fa", "#99e9f2", "#66d9e8", "#3bc9db", "#22b8cf", "#15aabf", "#1098ad", "#0c8599", "#0b7285"],
  teal: ["#e6fcf5", "#c3fae8", "#96f2d7", "#63e6be", "#38d9a9", "#20c997", "#12b886", "#0ca678", "#099268", "#087f5b"],
  green: ["#ebfbee", "#d3f9d8", "#b2f2bb", "#8ce99a", "#69db7c", "#51cf66", "#40c057", "#37b24d", "#2f9e44", "#2b8a3e"],
  yellow: ["#fff9db", "#fff3bf", "#ffec99", "#ffe066", "#ffd43b", "#fcc419", "#fab005", "#f59f00", "#f08c00", "#e67700"],
  orange: ["#fff4e6", "#ffe8cc", "#ffd8a8", "#ffc078", "#ffa94d", "#ff922b", "#fd7e14", "#f76707", "#e8590c", "#d9480f"],
};

export const NAMED_COLORS: Record<string, string> = {
  black: "#1e1e1e",
  white: "#ffffff",
  transparent: "transparent",
};

/** default digits when a bare color name is used (match Excalidraw's palette:
 * stroke = open-color 800, background = open-color 200) */
export const DEFAULT_STROKE_WEIGHT = 8;
export const DEFAULT_FILL_WEIGHT = 2;

/**
 * Resolve a KMD color token to a concrete color string, or null if invalid.
 * Tokens: `blue`, `blue.7`, `black`, `white`, `transparent`, `#rgb[a]`/`#rrggbb[aa]`.
 */
export const resolveColorToken = (
  token: string,
  context: "stroke" | "fill",
): string | null => {
  if (/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(token)) {
    return token.toLowerCase();
  }
  if (NAMED_COLORS[token] !== undefined) {
    return NAMED_COLORS[token];
  }
  const match = /^([a-z]+)(?:\.([1-9]))?$/.exec(token);
  if (!match) {
    return null;
  }
  const scale = OPEN_COLOR_SCALE[match[1]];
  if (!scale) {
    return null;
  }
  const digit = match[2]
    ? Number(match[2])
    : match[1] === "gray" && context === "fill"
      ? 1
      : context === "stroke"
        ? DEFAULT_STROKE_WEIGHT
        : DEFAULT_FILL_WEIGHT;
  return scale[digit];
};

export const FONT_SIZE_PX: Record<NonNullable<StyleProps["fontSize"]>, number> = {
  s: 16,
  m: 20,
  l: 28,
  xl: 36,
};

/** Document limits (spec §8). */
export const LIMITS = {
  fileBytes: 1_000_000,
  nodes: 2000,
  edges: 4000,
  nestingDepth: 12,
  labelChars: 2000,
  styleClasses: 100,
} as const;

export const closestMatches = (input: string, candidates: Iterable<string>, max = 3): string[] => {
  const distance = (a: string, b: string): number => {
    const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j++) {
      dp[0][j] = j;
    }
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
        );
      }
    }
    return dp[a.length][b.length];
  };
  return [...candidates]
    .map((c) => ({ c, d: distance(input.toLowerCase(), c.toLowerCase()) }))
    .filter(({ c, d }) => d <= Math.max(2, Math.floor(c.length / 2)))
    .sort((a, b) => a.d - b.d)
    .slice(0, max)
    .map(({ c }) => c);
};
